import { History, AlertTriangle } from "lucide-react";

// Friendly labels for the field keys carried in details.changes. Falls back
// to a humanised version of the raw key (snake_case → "Snake case").
const FIELD_LABELS = {
  assignee: "Assignee",
  status: "Status",
  priority: "Priority",
  title: "Title",
  system_category: "System",
  due_date: "Due date",
  description: "Description",
  cost: "Cost",
  notes: "Notes",
  // Logbook
  log_type: "Type",
  location: "Location",
  captain: "Captain",
  weather: "Weather",
  sea_state: "Sea state",
  fuel_level: "Fuel level",
  generator_hours: "Generator hours",
  engine_hours: "Engine hours",
  engine_readings: "Engine readings",
  departure: "Departure",
  arrival: "Arrival",
  distance_nm: "Distance (NM)",
  avg_speed: "Avg speed",
  // Petty cash
  amount: "Amount",
  currency: "Currency",
  category: "Category",
  date: "Date",
  vendor: "Vendor",
};

function fieldLabel(k) {
  return FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

// Render a diff value cleanly: strings as-is (no JSON quotes), empty/null as a
// dash, everything else via JSON for unambiguous display.
function fmtVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

/**
 * Shared audit-history timeline used by every detail modal that has a
 * "History" tab (maintenance/tasks, logbook, petty cash, supplier).
 *
 * Presentational only — the parent owns the `/audit?ref_kind=…&ref_id=…`
 * fetch and passes `rows`:
 *   - `null`  → still loading (spinner copy)
 *   - `[]`    → loaded, nothing to show (empty state)
 *   - `[…]`   → audit_log rows, rendered in the order given (parents pass
 *              the server's chronological order, oldest→newest)
 *
 * Each row renders its summary + actor + timestamp, plus a per-field
 * before→after diff when the event carries `details.changes`. Centralising
 * this keeps the history UX identical across the app (it was copy-pasted
 * verbatim in Logbook + PettyCash and diverged in Maintenance before).
 */
export function AuditTimeline({
  rows,
  emptyText = "No history yet. Future changes will appear here.",
  testidPrefix = "audit",
}) {
  if (rows === null) {
    return (
      <div className="text-sm text-zinc-500 py-4 text-center" data-testid={`${testidPrefix}-loading`}>
        Loading history…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="text-sm text-zinc-500 py-6 text-center" data-testid={`${testidPrefix}-empty`}>
        <History className="h-5 w-5 mx-auto text-zinc-300" />
        <div className="mt-2">{emptyText}</div>
      </div>
    );
  }
  // Dispute rounds get a dedicated styled entry. Round number comes from
  // details.dispute_round (written by the backend); older rows without it get
  // a running counter over the chronological order.
  let runningRound = 0;
  const disputeRounds = {};
  for (const row of rows) {
    if (row.action === "status_changed" && row.details?.to === "dispute") {
      runningRound += 1;
      disputeRounds[row.id] = row.details?.dispute_round || runningRound;
    }
  }
  return (
    <ol className="space-y-2" data-testid={`${testidPrefix}-list`}>
      {rows.map((row) => {
        const disputeRound = disputeRounds[row.id];
        if (disputeRound) {
          const reason = row.details?.note || (row.summary || "").split(" — ").slice(1).join(" — ");
          return (
            <li
              key={row.id}
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm"
              data-testid={`${testidPrefix}-dispute-row-${row.id}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-semibold text-red-700 inline-flex items-center gap-1.5 min-w-0">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Disputed — round {disputeRound}
                </span>
                <span className="text-[11px] text-red-500/80 font-mono">
                  {(row.created_at || "").replace("T", " ").slice(0, 16)}
                </span>
              </div>
              {reason && (
                <div
                  className="mt-1 text-[13px] text-red-800 break-words [overflow-wrap:anywhere]"
                  data-testid={`${testidPrefix}-dispute-reason-${row.id}`}
                >
                  “{reason}”
                </div>
              )}
              <div className="text-[12px] text-red-600/70 mt-0.5">
                by {row.by_user_name || row.by_user_email || "unknown"}
              </div>
            </li>
          );
        }
        return (
        <li
          key={row.id}
          className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm"
          data-testid={`${testidPrefix}-row-${row.id}`}
        >
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="font-medium min-w-0 break-words [overflow-wrap:anywhere]">{row.summary || row.action}</span>
            <span className="text-[11px] text-zinc-500 font-mono">
              {(row.created_at || "").replace("T", " ").slice(0, 16)}
            </span>
          </div>
          <div className="text-[12px] text-zinc-500 mt-0.5">
            by {row.by_user_name || row.by_user_email || "unknown"}
          </div>
          {row.details?.changes && Object.keys(row.details.changes).length > 0 && (
            <div className="mt-1.5 grid gap-1">
              {Object.entries(row.details.changes).map(([k, diff]) => (
                <div key={k} className="text-[12px] text-zinc-600">
                  <span className="text-[11px] font-medium text-zinc-500">{fieldLabel(k)}:</span>{" "}
                  <span className="text-zinc-400 line-through">{fmtVal(diff.from)}</span>
                  <span className="mx-1 text-zinc-400">→</span>
                  <span className="text-zinc-800">{fmtVal(diff.to)}</span>
                </div>
              ))}
            </div>
          )}
        </li>
        );
      })}
    </ol>
  );
}
